import { isValidElement, ReactNode } from "react";

/**
 * Recursively extracts text content from React children nodes.
 *
 * Features:
 * - Converts React children to plain text string
 * - Handles all React node types (strings, numbers, booleans, arrays, elements)
 * - Recursively processes nested React elements
 * - Extracts text from element children props
 * - Handles empty and null children gracefully
 * - Joins array children into single string
 * - Filters out non-text content automatically
 * - Returns empty string for unsupported node types
 *
 * Supported Node Types:
 * - String, number, boolean (converted to string)
 * - React elements (extracts children recursively)
 * - Arrays (processes each item and joins)
 * - Null, undefined (returns empty string)
 * - Objects (returns empty string)
 *
 * @param node - React node to convert to string
 * @returns Extracted text content as string, or empty string if no text content
 */
export function childrenToString(node: ReactNode | object): string {
  if (
    typeof node === "string" ||
    typeof node === "number" ||
    typeof node === "boolean"
  ) {
    return node.toString();
  }
  if (!node) {
    return "";
  }
  if (Array.isArray(node)) {
    if (!node.length) return "";
    return node.map((entry) => childrenToString(entry)).join("");
  }

  if (isValidElement(node)) {
    const children = node.props?.children;
    if (!children) return "";
    return childrenToString(children);
  }
  return "";
}
